import { useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  FIELD_CLASS,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import { formatDateTime, formatUsd } from '@/lib/format';
import { currentMonth } from './month';
import { useAiUsage } from './useAdmin';
import type { AiUsageReport } from './types';

/**
 * AI 비용.
 *
 * 이 프로젝트에서 유일하게 돈이 나가는 외부 호출이다. (CLAUDE.md 7절)
 * 예산 가드(AiUsageService)가 호출 전에 막지만, 사람이 눈으로 보는 자리도 필요하다 —
 * 가드가 잘못 설정돼 있으면 가드 자신은 그것을 알려주지 못한다.
 */
export function AiUsagePanel() {
  const [month, setMonth] = useState(currentMonth());
  const usageQuery = useAiUsage(month);

  return (
    <div className="flex flex-col gap-4">
      <Card
        title="AI 비용"
        action={
          <input
            type="month"
            className={cx(FIELD_CLASS, 'w-auto')}
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        }
      >
        {usageQuery.isLoading && <Spinner />}

        {usageQuery.error && (
          <ErrorState
            message={usageQuery.error.message}
            onRetry={() => void usageQuery.refetch()}
          />
        )}

        {usageQuery.data && <Summary report={usageQuery.data} />}
      </Card>

      {usageQuery.data && usageQuery.data.callCount > 0 && (
        <>
          <Card title="도메인별">
            <Table>
              <thead>
                <tr>
                  <Th>도메인</Th>
                  <Th align="right">호출</Th>
                  <Th align="right">비용</Th>
                </tr>
              </thead>
              <tbody>
                {usageQuery.data.byDomain.map((row) => (
                  <tr key={row.domain}>
                    <Td>{row.domain}</Td>
                    <Td align="right" numeric>
                      {row.callCount}
                    </Td>
                    <Td align="right" numeric>
                      {formatUsd(row.costUsd)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card title="최근 호출">
            <Table>
              <thead>
                <tr>
                  <Th>시각</Th>
                  <Th>대상</Th>
                  <Th align="right">입력</Th>
                  <Th align="right">출력</Th>
                  <Th align="right">비용</Th>
                </tr>
              </thead>
              <tbody>
                {usageQuery.data.recent.map((call, index) => (
                  <tr key={`${call.calledAt}-${index}`}>
                    <Td>
                      <span className="flex items-center gap-2">
                        {formatDateTime(call.calledAt)}
                        {!call.success && <Badge tone="danger">실패</Badge>}
                      </span>
                    </Td>
                    <Td>{call.targetRef ?? '-'}</Td>
                    <Td align="right" numeric>
                      {call.inputTokens.toLocaleString()}
                    </Td>
                    <Td align="right" numeric>
                      {call.outputTokens.toLocaleString()}
                    </Td>
                    <Td align="right" numeric>
                      {formatUsd(call.costUsd)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/*
              🔴 사고(thinking) 토큰이 출력에 포함되어 있다.
              Anthropic API 의 usage 가 분리해 주지 않는다. (00-architecture.md 4.1절)
            */}
            <p className="mt-3 text-xs text-fg-subtle">출력 토큰에는 사고 토큰이 포함되어 있습니다.</p>
          </Card>
        </>
      )}
    </div>
  );
}

function Summary({ report }: { report: AiUsageReport }) {
  if (report.callCount === 0) {
    return (
      <EmptyState
        title="이 달에는 호출이 없습니다."
        description="추천 분석 · 🔎 AI 2차 검토 · 회차 종합 분석 · 월간 리포트가 AI 를 부릅니다(거래마다 쓰던 모의투자 회고는 2026년 10월 18일부터 멈췄습니다). 이 달에 그런 일이 없었다면 정상입니다."
      />
    );
  }

  // 80% 를 넘으면 눈에 띄게 한다. 넘긴 뒤에 알면 이미 나간 돈이다.
  const 경고 = report.usageRatio >= 80;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="사용" value={formatUsd(report.totalCostUsd)} />
        <Metric label="예산" value={formatUsd(report.budgetUsd)} />
        <Metric
          label="사용률"
          value={`${report.usageRatio}%`}
          tone={경고 ? 'text-danger' : undefined}
        />
        <Metric label="호출" value={`${report.callCount}회`} />
      </div>

      {/* 예산 막대. 100%를 넘어도 막대가 넘치지 않게 자른다 */}
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-hover">
        <div
          className={cx('h-full', 경고 ? 'bg-danger' : 'bg-fg-muted')}
          style={{ width: `${Math.min(report.usageRatio, 100)}%` }}
        />
      </div>

      {report.failureCount > 0 && (
        <p className="text-xs text-warn">
          실패 {report.failureCount}회. 실패해도 토큰이 소비됐을 수 있습니다.
        </p>
      )}

      <p className="text-xs text-fg-subtle">
        하루 한도 {report.dailyLimit}회 · 입력 {report.inputTokens.toLocaleString()} · 출력{' '}
        {report.outputTokens.toLocaleString()} 토큰
      </p>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <p className="text-xs text-fg-muted">{label}</p>
      <p className={cx('text-sm font-semibold', tone ?? 'text-fg')}>{value}</p>
    </div>
  );
}

